<template>
  <div>
    <!-- Page Header -->
    <div class="mb-3 mt-1">
      <h3 class="font-weight-bolder">{{ $t('Integrations') }}</h3>
      <p class="text-muted">
        {{ $t('Connect marketplace and e-commerce platforms, automatically fetch orders.') }}
      </p>
    </div>

    <!-- Connected Integrations List -->
    <div v-if="connectedIntegrations.length > 0" class="mb-4">
      <h5 class="font-weight-bolder mb-2">{{ $t('Connected Integrations') }}</h5>
      <b-card
        v-for="(integration, index) in connectedIntegrations"
        :key="index"
        class="mb-2 shadow-none border"
        no-body
      >
        <b-card-body class="p-2 d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <!-- Platform Icon -->
            <div
              class="platform-icon-lg d-flex align-items-center justify-content-center rounded mr-2"
              :style="{
                backgroundColor: getPlatform(integration.platformName.toLowerCase()).bg,
                color: getPlatform(integration.platformName.toLowerCase()).hexColor
              }"
            >
              <span class="font-weight-bolder">{{
                getPlatform(integration.platformName.toLowerCase()).code
              }}</span>
            </div>

            <!-- Platform Details -->
            <div>
              <h5 class="mb-25 font-weight-bolder">{{ integration.platformName }}</h5>
              <small class="text-muted">
                {{ $t('Connected at:') }} {{ formatDate(integration.created_at) }} &middot;
                {{ integration.vendor_id }}
              </small>
            </div>
          </div>

          <div class="d-flex align-items-center">
            <div class="status-badge-active mr-2">
              <span class="status-dot"></span> {{ $t('Active') }}
            </div>
            <b-button
              variant="outline-secondary btn-light-danger"
              size="sm"
              @click="confirmDisconnect(integration)"
            >
              {{ $t('Disconnect') }}
            </b-button>
          </div>
        </b-card-body>
      </b-card>
    </div>

    <!-- Platform Grid -->
    <div>
      <h5 class="font-weight-bolder mb-2">{{ $t('Add new integration') }}</h5>
      <b-row>
        <b-col md="3" v-for="platform in platforms" :key="platform.id" class="mb-3">
          <b-card
            class="h-100 shadow-none border platform-card"
            :class="{
              'cursor-pointer': platform.status === 'active' && !isConnected(platform.id),
              'platform-coming-soon': platform.status === 'coming_soon'
            }"
            @click="handlePlatformClick(platform)"
            no-body
          >
            <b-card-body class="p-2 d-flex flex-column h-100 relative">
              <!-- Top Row: Icon & Badge -->
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div
                  class="platform-icon d-flex align-items-center justify-content-center rounded"
                  :style="{ backgroundColor: platform.bg, color: platform.hexColor }"
                >
                  <span class="font-weight-bolder">{{ platform.code }}</span>
                </div>

                <b-badge
                  v-if="platform.status === 'coming_soon'"
                  variant="dark"
                  class="text-muted px-2 py-1 badge-2 card"
                >
                  {{ $t('Coming Soon') }}
                </b-badge>
                <b-badge
                  v-else-if="isConnected(platform.id)"
                  variant="primary"
                  class="px-2 py-1 badge-1 bg-light-primary text-primary"
                >
                  {{ $t('Connected') }}
                </b-badge>
              </div>

              <!-- Bottom Content -->
              <div class="mt-auto">
                <h5 class="font-weight-bolder mb-25">{{ platform.name }}</h5>
                <small class="text-muted d-block mb-1">{{ platform.type }}</small>

                <div v-if="platform.status === 'active' && !isConnected(platform.id)">
                  <a
                    href="#"
                    class="text-primary font-weight-bold text-decoration-none small"
                    @click.prevent
                    v-html="$t('Connect') + ' &rarr;'"
                  >
                  </a>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- Connect Modal -->
    <b-modal
      id="connect-modal"
      ref="connectModal"
      hide-footer
      centered
      content-class="border-0 shadow"
    >
      <template #modal-header="{ close }">
        <div class="d-flex align-items-center w-100">
          <div
            class="platform-icon mr-2 d-flex align-items-center justify-content-center rounded"
            v-if="selectedPlatform"
            :style="{ backgroundColor: selectedPlatform.bg, color: selectedPlatform.hexColor }"
          >
            <span class="font-weight-bolder">{{ selectedPlatform.code }}</span>
          </div>
          <div>
            <h5 class="mb-0 font-weight-bolder">
              {{ selectedPlatform ? selectedPlatform.name : '' }} {{ $t('connect your store') }}
            </h5>
            <small class="text-muted">{{ $t('Integration Setup') }}</small>
          </div>
          <button
            type="button"
            class="border-0 bg-transparent text-body ml-auto cursor-pointer"
            style="font-size: 2rem; outline: none"
            aria-label="Close"
            @click="close()"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      </template>

      <validation-observer ref="connectForm">
        <b-form @submit.prevent="connectIntegration">
          <b-form-group
            label="Vendor ID"
            label-for="store-id"
            label-class=" small font-weight-bold"
          >
            <validation-provider #default="{ errors }" rules="required" name="Vendor ID">
              <b-form-input
                id="store-id"
                v-model="connectForm.storeId"
                :state="errors.length > 0 ? false : null"
                placeholder="A00012345"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            label="Access Key"
            label-for="access-key"
            label-class=" small font-weight-bold"
          >
            <validation-provider #default="{ errors }" rules="required" name="Access Key">
              <b-form-input
                id="access-key"
                v-model="connectForm.accessKey"
                :state="errors.length > 0 ? false : null"
                :placeholder="$t('Enter Access Key')"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            label="Secret Key"
            label-for="secret-key"
            label-class=" small font-weight-bold"
          >
            <validation-provider #default="{ errors }" rules="required" name="Secret Key">
              <div class="position-relative">
                <b-form-input
                  id="secret-key"
                  :type="showSecretKey ? 'text' : 'password'"
                  v-model="connectForm.secretKey"
                  :state="errors.length > 0 ? false : null"
                  :placeholder="$t('Enter Secret Key')"
                />
                <span
                  class="password-toggle cursor-pointer"
                  @click="showSecretKey = !showSecretKey"
                >
                  👁️
                </span>
              </div>
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-button
            variant="primary"
            type="submit"
            block
            class="mt-4 font-weight-bold"
            :disabled="loading"
          >
            {{ loading ? $t('Loading') || 'Yükleniyor...' : $t('Establish Connection') }}
          </b-button>
        </b-form>
      </validation-observer>
    </b-modal>

    <!-- Disconnect Modal -->
    <b-modal
      id="disconnect-modal"
      ref="disconnectModal"
      centered
      hide-header-close
      hide-footer
      content-class="border-0 shadow disconnect-modal"
    >
      <div class="text-center p-3">
        <h4 class="font-weight-bolder mb-3">{{ $t('Disconnect?') }}</h4>
        <p class="text-muted mb-4">
          {{ integrationToDisconnect ? integrationToDisconnect.platformName : '' }}
          {{ $t('integration will be removed. Existing orders will not be affected.') }}
        </p>
        <div class="d-flex justify-content-center">
          <b-button
            variant="outline-secondary"
            class="mr-2 px-4"
            @click="$refs.disconnectModal.hide()"
            :disabled="loading"
          >
            {{ $t('Cancel') }}
          </b-button>
          <b-button
            variant="danger"
            class="btn-light-danger px-4"
            @click="handleDisconnect"
            :disabled="loading"
          >
            {{ loading ? $t('Loading') || 'Yükleniyor...' : $t('Disconnect') }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BBadge,
  BButton,
  BModal,
  BForm,
  BFormGroup,
  BFormInput
} from 'bootstrap-vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { required } from '@validations';
import useService from '@/auth/service/useService';
import { helper } from '@core/mixins/helper';

export default {
  mixins: [helper],
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BBadge,
    BButton,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    ValidationObserver,
    ValidationProvider
  },
  data() {
    return {
      connectedIntegrations: [],
      platforms: [
        {
          id: 'coupang',
          name: 'Coupang',
          status: 'active',
          code: 'CO',
          hexColor: '#ea5455',
          bg: 'rgba(234, 84, 85, 0.12)',
          type: 'API Key'
        },
        {
          id: 'shopify',
          name: 'Shopify',
          status: 'coming_soon',
          code: 'SH',
          hexColor: '#28c76f',
          bg: 'rgba(40, 199, 111, 0.12)',
          type: 'OAuth 2.0'
        },
        {
          id: 'etsy',
          name: 'Etsy',
          status: 'coming_soon',
          code: 'ET',
          hexColor: '#ff9f43',
          bg: 'rgba(255, 159, 67, 0.12)',
          type: 'OAuth 2.0'
        },
        {
          id: 'ikas',
          name: 'ikas',
          status: 'coming_soon',
          code: 'IK',
          hexColor: '#7367f0',
          bg: 'rgba(115, 103, 240, 0.12)',
          type: 'API Key'
        },
        {
          id: 'woocommerce',
          name: 'WooCommerce',
          status: 'coming_soon',
          code: 'WO',
          hexColor: '#7367f0',
          bg: 'rgba(115, 103, 240, 0.12)',
          type: 'API Key'
        },
        {
          id: 'ticimax',
          name: 'Ticimax',
          status: 'coming_soon',
          code: 'TI',
          hexColor: '#ea5455',
          bg: 'rgba(234, 84, 85, 0.12)',
          type: 'API Key'
        },
        {
          id: 'amazon',
          name: 'Amazon',
          status: 'coming_soon',
          code: 'AM',
          hexColor: '#ff9f43',
          bg: 'rgba(255, 159, 67, 0.12)',
          type: 'OAuth 2.0'
        },
        {
          id: 'ebay',
          name: 'eBay',
          status: 'coming_soon',
          code: 'EB',
          hexColor: '#ea5455',
          bg: 'rgba(234, 84, 85, 0.12)',
          type: 'OAuth 2.0'
        }
      ],
      selectedPlatform: null,
      integrationToDisconnect: null,
      connectForm: {
        storeId: '',
        accessKey: '',
        secretKey: ''
      },
      showSecretKey: false,
      loading: false,
      required
    };
  },
  async created() {
    await this.fetchCoupangVendor();
  },
  methods: {
    getPlatform(id) {
      return this.platforms.find(p => p.id === id) || {};
    },
    isConnected(platformId) {
      return this.connectedIntegrations.some(
        i => i.platformName?.toLowerCase() === platformId?.toLowerCase()
      );
    },
    handlePlatformClick(platform) {
      if (platform.status === 'active' && !this.isConnected(platform.id)) {
        this.selectedPlatform = platform;
        this.connectForm = { storeId: '', accessKey: '', secretKey: '' };
        this.showSecretKey = false;
        this.$refs.connectModal.show();
      }
    },
    async fetchCoupangVendor() {
      try {
        this.loading = true;
        const response = await useService.getCoupangVendor();
        const data = response?.data;
        this.connectedIntegrations = data?.is_enabled ? [{ ...data, platformName: 'Coupang' }] : [];
      } catch {
        this.connectedIntegrations = [];
      } finally {
        this.loading = false;
      }
    },
    connectIntegration() {
      this.$refs.connectForm.validate().then(async success => {
        if (success) {
          if (this.selectedPlatform) {
            try {
              this.loading = true;
              await useService.connectCoupangVendor({
                access_key: this.connectForm.accessKey,
                secret_key: this.connectForm.secretKey,
                vendor_id: this.connectForm.storeId,
                is_enabled: true
              });

              await this.fetchCoupangVendor();
              this.$refs.connectModal.hide();
              this.selectedPlatform = null;
              this.showSuccessMessage(
                this.$t('Success') || 'Başarılı',
                'Bağlantı başarıyla kuruldu.'
              );
            } catch (error) {
              this.showErrorMessage('Bağlantı kurulamadı. Bilgilerinizi kontrol edin.');
            } finally {
              this.loading = false;
            }
          }
        }
      });
    },
    confirmDisconnect(integration) {
      this.integrationToDisconnect = integration;
      this.$refs.disconnectModal.show();
    },
    async handleDisconnect() {
      if (this.integrationToDisconnect) {
        try {
          this.loading = true;
          await useService.toggleVendorStatus(false);

          await this.fetchCoupangVendor();
          this.integrationToDisconnect = null;
          this.$refs.disconnectModal.hide();
          this.showSuccessMessage(this.$t('Success') || 'Başarılı', 'Bağlantı başarıyla kesildi.');
        } catch (error) {
          this.showErrorMessage('Bağlantı kesilemedi.');
        } finally {
          this.loading = false;
        }
      }
    },
    formatDate(date) {
      return date ? new Date(date).toLocaleDateString('tr-TR') : '';
    }
  }
};
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}

.platform-card {
  border-radius: 12px;
  transition: all 0.2s ease-in-out;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
  }
}

.platform-coming-soon {
  opacity: 0.85;
  filter: grayscale(75%);
  pointer-events: none;
  background-image: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 10px,
    rgba(0, 0, 0, 0.02) 10px,
    rgba(0, 0, 0, 0.02) 20px
  );
}

.platform-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px !important;
  font-size: 1.1rem;
}

.platform-icon-lg {
  width: 48px;
  height: 48px;
  border-radius: 12px !important;
  font-size: 1.25rem;
}

.badge-2 {
  background-color: #f3f3f3;
  color: #a8a8a8 !important;
  font-weight: 600;
  border-radius: 6px;
}

.bg-light-primary {
  background-color: rgba(115, 103, 240, 0.12) !important;
}

.badge-1 {
  font-weight: 600;
  border-radius: 6px;
}

.status-badge-active {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  border: 1px solid #28c76f;
  color: #28c76f;
  font-weight: 600;
  font-size: 0.85rem;
  background-color: #fff;
}

.status-badge-inactive {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  border: 1px solid #ea5455;
  color: #ea5455;
  font-weight: 600;
  font-size: 0.85rem;
  background-color: #fff;

  .status-dot {
    background-color: #ea5455;
  }
}

.status-dot {
  width: 6px;
  height: 6px;
  background-color: #28c76f;
  border-radius: 50%;
  margin-right: 6px;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6e6b7b;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
}

.btn-light-danger {
  background-color: rgba(234, 84, 85, 0.12) !important;
  color: #ea5455 !important;
  border: none;

  &:hover {
    background-color: #ea5455 !important;
    color: #fff !important;
  }
}

.modal-header {
  border-bottom: none !important;
}

.disconnect-modal {
  border-radius: 12px;
}

body.dark-layout {
  .badge-2 {
    background-color: #3b4253 !important;
    color: #b4b7bd !important;
  }

  .status-badge-active,
  .status-badge-inactive {
    background-color: transparent !important;
  }

  .platform-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25) !important;
  }

  .platform-coming-soon {
    background-image: repeating-linear-gradient(
      45deg,
      transparent,
      transparent 10px,
      rgba(255, 255, 255, 0.02) 10px,
      rgba(255, 255, 255, 0.02) 20px
    );
  }
}
</style>
